import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { format } from "date-fns";
import { escapeRegExp } from "../../../lib/utils";
import { WatchActions } from "../../../state";

const prettyField = (key) =>
  key
    .replace(/_ids?$/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const prettyVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.length ? `[${v.join(", ")}]` : "—";
  let s = String(v)
    .replace(/<[^>]*>/g, " ")
    .replace(/&[a-z#0-9]+;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (s.length > 80) s = s.slice(0, 80) + "…";
  return s === "" ? "—" : s;
};

// Inline list mirrored from Dial.jsx (not part of the Redux store).
const LUMINESCENCE = [
  { id: 1, label: "Extremely visible" },
  { id: 2, label: "Standard visibility" },
  { id: 3, label: "Basically visible" },
  { id: 4, label: "Weak visibility" },
];

// Maps each stored `_id`/`_ids` field to the `watchLists` list that holds its
// label. Several fields borrow another attribute's pool (shared pools), so the
// `list`/`val`/`label` keys often differ from the field name — these mirror the
// exact bindings used by the edit-form section components.
const LABEL_MAP = {
  // model / pricing / status
  brand_id: { list: "brand", val: "brand_id", label: "brand" },
  series_id: { list: "series", val: "series_id", label: "series" },
  size_id: { list: "size", val: "size_id", label: "size" },
  status_id: { list: "status", val: "status_id", label: "status" },
  warranty_id: { list: "warranty", val: "warranty_id", label: "warranty" },
  delivery_id: { list: "delivery", val: "delivery_id", label: "delivery" },
  vendor_id: { list: "vendor", val: "vendor_id", label: "vendor" },
  ship_tier_id: { list: "ship_tier", val: "ship_tier_id", label: "ship_tier" },
  // dial
  dial_color_id: { list: "dial_color", val: "dial_color_id", label: "dial_color" },
  subdial_color_id: { list: "dial_color", val: "dial_color_id", label: "dial_color" },
  dial_flange_color_id: { list: "dial_color", val: "dial_color_id", label: "dial_color" },
  dial_finish_id: { list: "dial_finish", val: "dial_finish_id", label: "dial_finish" },
  hour_marker_type_id: { list: "hour_marker_type", val: "hour_marker_type_id", label: "hour_marker_type" },
  hour_marker_id: { list: "hour_marker", val: "hour_marker_id", label: "hour_marker" },
  hour_marker_color_id: { list: "hour_marker_color", val: "hour_marker_color_id", label: "hour_marker_color" },
  hour_marker_finish_id: { list: "hands_finish", val: "hands_finish_id", label: "hands_finish" },
  hour_marker_material_id: { list: "hands_material", val: "hands_material_id", label: "hands_material" },
  track_type_id: { list: "track_type", val: "track_type_id", label: "track_type" },
  hands_material_id: { list: "hands_material", val: "hands_material_id", label: "hands_material" },
  hands_color_id: { list: "hands_color", val: "hands_color_id", label: "hands_color" },
  hands_finish_id: { list: "hands_finish", val: "hands_finish_id", label: "hands_finish" },
  second_hand_id: { list: "second_hand", val: "second_hand_id", label: "second_hand" },
  second_hand_color_id: { list: "hands_color", val: "hands_color_id", label: "hands_color" },
  second_hand_material_id: { list: "hands_material", val: "hands_material_id", label: "hands_material" },
  flange_indication_id: { list: "flange_indication", val: "flange_indication_id", label: "flange_indication" },
  date_indicator_id: { list: "date_indicator", val: "date_indicator_id", label: "date_indicator" },
  date_location_id: { list: "date_location", val: "date_location_id", label: "date_location" },
  luminescence_id: { hardcoded: LUMINESCENCE },
  // band & buckle
  band_name_id: { list: "band_name", val: "band_name_id", label: "band_name" },
  band_material_id: { list: "band_material", val: "band_material_id", label: "band_material" },
  band_color_id: { list: "band_color", val: "band_color_id", label: "band_color" },
  clasp_name_id: { list: "clasp_name", val: "clasp_name_id", label: "clasp_name" },
  clasp_type_id: { list: "clasp_type", val: "clasp_type_id", label: "clasp_type" },
  clasp_characteristic_id: { list: "clasp_characteristic", val: "clasp_characteristic_id", label: "clasp_characteristic" },
  clasp_material_ids: { list: "material", val: "case_material_id", label: "case_material", multi: true },
  clasp_characteristic_ids: { list: "clasp_characteristic", val: "clasp_characteristic_id", label: "clasp_characteristic", multi: true },
  // case / bezel
  case_material_id: { list: "material", val: "case_material_id", label: "case_material" },
  case_color_id: { list: "bezel_color", val: "bezel_color_id", label: "bezel_color" },
  case_finish_id: { list: "case_finish", val: "case_finish_id", label: "case_finish" },
  bezel_id: { list: "bezel", val: "bezel_id", label: "bezel" },
  bezel_color_id: { list: "bezel_color", val: "bezel_color_id", label: "bezel_color" },
  bezel_finish_id: { list: "case_finish", val: "case_finish_id", label: "case_finish" },
  bezel_material_id: { list: "material", val: "case_material_id", label: "case_material" },
  bezel_material_ids: { list: "material", val: "case_material_id", label: "case_material", multi: true },
  bezel_characteristics_ids: { list: "bezel_characteristics", val: "bezel_characteristics_id", label: "bezel_characteristics", multi: true },
  crown_material_id: { list: "material", val: "case_material_id", label: "case_material" },
  button_material_id: { list: "material", val: "case_material_id", label: "case_material" },
  crystal_id: { list: "crystal", val: "crystal_id", label: "crystal" },
  case_back_id: { list: "case_back", val: "case_back_id", label: "case_back" },
  case_shape_id: { list: "case_shape", val: "case_shape_id", label: "case_shape" },
  // movement
  calibre_id: { list: "calibre", val: "calibre_id", label: "calibre" },
  base_calibre: { list: "calibre", val: "calibre_id", label: "calibre" },
  movement_type_id: { list: "calibre_movement_type", val: "movement_id", label: "movement" },
  item_certification_ids: { list: "certification", val: "certification_id", label: "certification", multi: true },
  item_complications: { list: "calibre_complication", val: "complication_id", label: "complication", objKey: "complication_id", multi: true },
};

const lookupLabel = (map, id, lists) => {
  if (id === null || id === undefined || id === "") return null;
  if (map.hardcoded) {
    const f = map.hardcoded.find((x) => x.id === Number(id));
    return f ? f.label : null;
  }
  const arr = lists?.[map.list];
  if (!Array.isArray(arr)) return null;
  const f = arr.find((x) => Number(x[map.val]) === Number(id));
  return f ? f[map.label] : null;
};

const formatValue = (field, value, lists) => {
  const map = LABEL_MAP[field];
  if (Array.isArray(value)) {
    if (!value.length) return "—";
    if (map?.objKey) {
      const names = value.map(
        (o) => lookupLabel(map, o?.[map.objKey], lists) || o?.[map.objKey]
      );
      return `[${names.join(", ")}]`;
    }
    if (map) {
      const names = value.map((id) => lookupLabel(map, id, lists) || id);
      return `[${names.join(", ")}]`;
    }
    // arrays of plain objects with no mapping (e.g. additional bands)
    return `${value.length} item${value.length > 1 ? "s" : ""}`;
  }
  if (map) {
    const label = lookupLabel(map, value, lists);
    if (label) return label;
  }
  return prettyVal(value);
};

// The item_site_fields_log `data` column holds two very different formats:
//   - New admin (items.js): a JSON snapshot (or {__delta}) — starts with { or [
//   - Legacy storefront: a human-readable pipe-delimited delta string, e.g.
//       "Price Retail: 6400.00 => 6900.00 | Status: Disabled => Active | "
//     plus an older single-value variant "Price On Special => 0 | ".
// isJsonString distinguishes them; parseLegacyDelta handles the legacy string.
const isJsonString = (s) => {
  if (typeof s !== "string") return false;
  const t = s.trim();
  return t.startsWith("{") || t.startsWith("[");
};

// Parse a legacy pipe-delimited delta string into ready-to-render chip strings
// ("Label: old → new", or "Label: value" for the older single-value variant).
// Values in the legacy data are already human-readable (resolved labels, Yes/No,
// dates), so no lookup is needed. Empty sides become "—" (rendered as "empty").
const parseLegacyDelta = (raw) => {
  const disp = (x) => {
    const t = (x == null ? "" : String(x)).trim();
    return t === "" ? "—" : t;
  };
  return String(raw)
    .split(" | ")
    .map((seg) => seg.trim())
    .filter(Boolean)
    .map((seg) => {
      const idx = seg.indexOf("=>");
      if (idx === -1) return seg; // malformed — surface raw rather than drop it
      const left = seg.slice(0, idx).trim();
      const right = seg.slice(idx + 2).trim();
      const colon = left.indexOf(":");
      // No colon in the label part => older "Label => value" (new value only).
      if (colon === -1) return `${left}: ${disp(right)}`;
      return `${left.slice(0, colon).trim()}: ${disp(left.slice(colon + 1))} → ${disp(right)}`;
    })
    // Drop internal "Changed By: host → host" churn and both-empty no-ops.
    .filter((chip) => !/^Changed By:/i.test(chip) && !/: — → —$/.test(chip));
};

// null / undefined / "" / whitespace-only / empty array all mean "no value".
// New fields drift between these representations across snapshots (a save writes
// null, the key is absent in an older snapshot, etc.), which must NOT count as a
// change — otherwise every unrelated edit logs a spurious "Field empty → empty".
const isBlank = (v) =>
  v === null ||
  v === undefined ||
  (typeof v === "string" && v.trim() === "") ||
  (Array.isArray(v) && v.length === 0);

const diffSnapshots = (prev, curr, lists) => {
  const changes = [];
  const keys = new Set([
    ...Object.keys(prev || {}),
    ...Object.keys(curr || {}),
  ]);
  keys.forEach((k) => {
    const a = prev ? prev[k] : undefined;
    const b = curr[k];
    if (isBlank(a) && isBlank(b)) return; // both empty → not a real change
    if (JSON.stringify(a) !== JSON.stringify(b)) {
      changes.push(
        `${prettyField(k)}: ${formatValue(k, a, lists)} → ${formatValue(
          k,
          b,
          lists
        )}`
      );
    }
  });
  return changes;
};

// Renders the change pills for one history row. In a table cell (no fixed row
// height) the row grows with content: 1 line stays short, a 2-line wrap expands
// to fit, and 3+ lines cap at maxHeight and scroll.
const EditsCell = ({ items }) => {
  const list = Array.isArray(items) ? items : [items];
  return (
    <div
      title={list.join("\n")}
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-start",
        alignContent: "flex-start",
        gap: 8,
        maxHeight: 64,
        overflowY: "auto",
        width: "100%",
      }}
    >
      {list.map((t, i) => {
        const sep = t.indexOf(":");
        const label = sep > -1 ? t.slice(0, sep) : t;
        const rest = sep > -1 ? t.slice(sep + 1).trim() : "";
        const [oldVal, newVal] =
          rest.indexOf(" → ") > -1 ? rest.split(" → ") : [rest, null];
        const renderVal = (v) =>
          v === "—" ? (
            <span style={{ fontStyle: "italic", color: "#9aa4b1" }}>empty</span>
          ) : (
            v
          );
        return (
          <span
            key={i}
            style={{
              display: "inline-flex",
              alignItems: "center",
              background: "#eef1f5",
              border: "1px solid #d4dae3",
              borderRadius: 4,
              padding: "1px 8px",
              fontSize: 12,
              lineHeight: 1.5,
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ fontWeight: 600, color: "#3a4654" }}>{label}</span>
            {rest && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  marginLeft: 5,
                }}
              >
                {newVal !== null ? (
                  <>
                    {/* Three readable tiers: bold gray label (the anchor),
                        normal-weight gray old value, and a blue-accent new value
                        — distinguished from the label by color, not just weight,
                        so the eye reads field → was → now without skipping. */}
                    <span style={{ color: "#5a6573" }}>{renderVal(oldVal)}</span>
                    <span
                      style={{
                        margin: "0 5px",
                        color: "#aab2bd",
                        fontSize: 13,
                        lineHeight: 1,
                      }}
                    >
                      →
                    </span>
                    <span style={{ color: "#1c6ea4", fontWeight: 600 }}>
                      {renderVal(newVal)}
                    </span>
                  </>
                ) : (
                  // single-value legacy row — it IS the current value, so accent it
                  <span style={{ color: "#1c6ea4", fontWeight: 600 }}>
                    {renderVal(oldVal)}
                  </span>
                )}
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
};

export const WatchHistory = (props) => {
  const {
    pageRoute: {
      params: { id },
    },
  } = props;
  const [data, setData] = useState();
  const siteId = useSelector((state) => state.scopes.site_id);
  const lists = useSelector((state) => state.watches.watchLists);
  const dispatch = useDispatch();

  useEffect(() => {
    (async function () {
      const data = await WatchActions.getHistory(id, siteId);
      setData(data);
    })();
    // Ensure attribute lists are loaded when landing directly on /history/:id,
    // so stored ids can be resolved to readable labels.
    if (!lists?.brand?.length) {
      dispatch(WatchActions.getAllLists());
      dispatch(
        WatchActions.getList([
          "brand",
          "series",
          "size",
          "warranty",
          "status",
          "vendor",
          "ship_tier",
          "delivery",
          "material",
          "edition",
        ])
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  let historyRows = [];
  if (data?.history.length) {
    const sorted = [...data.history].sort(
      (a, b) =>
        new Date(a.date_changed) - new Date(b.date_changed) ||
        a.log_id - b.log_id
    );
    let prevSnap = null;
    historyRows = sorted.map((row, i) => {
      // Three stored formats coexist (see comment block by isJsonString above):
      //   - new admin (edit-watch): JSON snapshot of full watch state
      //   - new admin (watch grid): JSON {__delta: {field: {from, to}}} —
      //     partial edits; rendered standalone so they never enter the
      //     snapshot diff chain (a partial snapshot would corrupt it)
      //   - legacy storefront: pipe-delimited "Label: old => new | ..." delta
      // Snapshot rows feed the prev/curr diff; delta rows render directly.
      let snap = null;
      let legacyChanges = null;
      let jsonDelta = null;
      if (typeof row.data !== "string") {
        snap = row.data || {};
      } else if (isJsonString(row.data)) {
        try { snap = JSON.parse(row.data); } catch (e) { snap = {}; }
      } else {
        legacyChanges = parseLegacyDelta(row.data);
      }
      if (snap && snap.__delta) {
        jsonDelta = snap.__delta;
        snap = null;
      }

      let changes;
      if (jsonDelta !== null) {
        // Grid-edit delta row — like legacy rows, does NOT update prevSnap.
        const entries = Object.entries(jsonDelta);
        changes = entries.length
          ? entries.map(
              ([k, d]) =>
                `${prettyField(k)}: ${formatValue(k, d?.from, lists)} → ${formatValue(k, d?.to, lists)}`
            )
          : ["No field changes"];
      } else if (legacyChanges !== null) {
        // Legacy row IS the delta — don't run snapshot diff against previous.
        // Don't update prevSnap either; the next new-format row will treat its
        // own state as the initial snapshot (one-time "Initial record" at the
        // old-to-new transition, acceptable cost for keeping all data renderable).
        changes = legacyChanges.length ? legacyChanges : ["No field changes"];
      } else {
        // prevSnap === null means there is no earlier JSON snapshot to diff
        // against. Two very different cases:
        //   - i === 0: genuinely the item's first-ever history row → "Initial record"
        //   - i > 0: a first structured snapshot sitting on top of older
        //     legacy/grid rows. Changes can't be itemized against a legacy delta,
        //     and in practice this is a no-op "update" (e.g. bumping updated_at
        //     for sync). Labeling it "Initial record" is wrong for an item that
        //     already has years of history, so show "No field changes" instead —
        //     consistent with how later no-op saves already render.
        changes =
          prevSnap === null
            ? i === 0
              ? ["Initial record"]
              : ["No field changes"]
            : diffSnapshots(prevSnap, snap, lists);
        prevSnap = snap;
      }

      return {
        id: format(new Date(row.date_changed), "T"),
        user: row.changed_by,
        date: format(new Date(row.date_changed), "PPpp"),
        edits: changes.length ? changes : ["No field changes"],
      };
    });
  }
  const [searchText, setSearchText] = useState("");
  const [rows, setRows] = useState(historyRows);
  const requestSearch = (searchValue) => {
    setSearchText(searchValue);
    const searchRegex = new RegExp(escapeRegExp(searchValue), "i");
    const filteredRows = historyRows.filter((row) => {
      return Object.keys(row).some((field) => {
        return searchRegex.test(row[field]?.toString());
      });
    });
    setRows(filteredRows);
  };
  // Re-seed rows whenever the source history or the lookup lists change (lists
  // often arrive a tick after the history), unless the user is searching.
  useEffect(() => {
    if (!searchText) {
      setRows(historyRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, lists, searchText]);

  // Newest-first. `id` is the epoch-ms of date_changed, so numeric desc works.
  const displayRows = [...rows].sort((a, b) => Number(b.id) - Number(a.id));
  const th = {
    padding: "8px 12px",
    fontSize: 12,
    fontWeight: 600,
    color: "#3a4654",
    textAlign: "left",
    // Sticky header: stays pinned while the history list scrolls. A solid
    // background keeps rows from showing through, and the bottom rule uses an
    // inset box-shadow (a border-collapse border doesn't render while sticky).
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: "#ffffff",
    boxShadow: "inset 0 -2px 0 #e3e8ee",
  };
  const td = { padding: "7px 12px", verticalAlign: "top", fontSize: 13 };

  return (
    <div style={{ width: "100%" }}>
      <input
        value={searchText}
        onChange={(e) => requestSearch(e.target.value)}
        placeholder="Search history…"
        style={{
          padding: "7px 10px",
          margin: "4px 0 12px",
          width: 280,
          maxWidth: "100%",
          border: "1px solid #cfd6df",
          borderRadius: 4,
          fontSize: 13,
          boxSizing: "border-box",
        }}
      />
      <table
        style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}
      >
        <colgroup>
          <col style={{ width: 200 }} />
          <col />
          <col style={{ width: 130 }} />
        </colgroup>
        <thead>
          <tr>
            <th style={th}>Date Changed</th>
            <th style={th}>Edits</th>
            <th style={th}>Changed By</th>
          </tr>
        </thead>
        <tbody>
          {displayRows.map((r) => (
            <tr key={r.id} style={{ borderBottom: "1px solid #eef1f5" }}>
              <td style={{ ...td, whiteSpace: "nowrap", color: "#5a6573" }}>
                {r.date}
              </td>
              <td style={td}>
                <EditsCell items={r.edits} />
              </td>
              <td style={{ ...td, color: "#5a6573" }}>{r.user}</td>
            </tr>
          ))}
          {displayRows.length === 0 && (
            <tr>
              <td colSpan={3} style={{ padding: 16, color: "#9aa4b1" }}>
                No history.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <br />
    </div>
  );
};
